/**
 * OAuth browser flow for @sailfish-ai/autofix
 *
 * 1. Start local HTTP server on random port
 * 2. Open browser to Sailfish auth URL with callback
 * 3. Receive auth code via redirect
 * 4. Exchange for JWT
 * 5. Write credentials
 */

import { createServer, type IncomingMessage, type ServerResponse } from "http";
import { URL } from "url";
import { SAILFISH_AUTH_URL, SAILFISH_GRAPHQL_URL } from "../constants.js";
import { readCredentials, writeCredentials, type Credentials } from "./credentials.js";

/**
 * Run the OAuth browser flow. Returns credentials on success.
 */
export async function oauthBrowserFlow(): Promise<Credentials> {
  return new Promise((resolve, reject) => {
    const server = createServer(async (req: IncomingMessage, res: ServerResponse) => {
      try {
        const url = new URL(req.url || "/", `http://localhost`);

        if (url.pathname === "/callback") {
          const token = url.searchParams.get("token");
          const graphqlUrl = url.searchParams.get("graphql_url") || SAILFISH_GRAPHQL_URL;

          if (!token) {
            res.writeHead(400, { "Content-Type": "text/html" });
            res.end("<h1>Authentication failed</h1><p>No token received. Please try again.</p>");
            reject(new Error("No token received from auth callback"));
            return;
          }

          const creds: Credentials = {
            jwt: token,
            graphqlUrl,
            updatedAt: Date.now(),
            source: "cli",
            // Re-auth must not forget which agents the user set up
            agents: readCredentials()?.agents,
          };

          writeCredentials(creds);

          res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
          res.end(
            "<h1>Authenticated!</h1>" +
            "<p>You can close this tab and return to your terminal.</p>" +
            "<script>window.close()</script>"
          );

          server.close();
          resolve(creds);
        } else {
          res.writeHead(404);
          res.end("Not found");
        }
      } catch (err) {
        res.writeHead(500);
        res.end("Internal error");
        reject(err);
      }
    });

    server.listen(0, "127.0.0.1", async () => {
      const addr = server.address();
      if (!addr || typeof addr === "string") {
        reject(new Error("Failed to start local server"));
        return;
      }

      const callbackUrl = `http://127.0.0.1:${addr.port}/callback`;
      const authUrl = `${SAILFISH_AUTH_URL}?callback=${encodeURIComponent(callbackUrl)}`;

      console.log(`\nOpening browser for authentication...`);
      console.log(`If it doesn't open, visit: ${authUrl}\n`);

      try {
        const open = (await import("open")).default;
        await open(authUrl);
      } catch {
        console.log(`Could not open browser automatically.`);
        console.log(`Please open this URL manually: ${authUrl}`);
      }
    });

    // Timeout after 5 minutes
    setTimeout(() => {
      server.close();
      reject(new Error("Authentication timed out after 5 minutes"));
    }, 5 * 60 * 1000);
  });
}

/**
 * Exchange a short-lived token (from web app "copy command") for full credentials.
 * The web app passes the endpoint the token was issued for via --graphql-url;
 * without it, a staging token would be sent to the production default and
 * rejected by JWT validation.
 */
export async function exchangeToken(token: string, graphqlUrlOverride?: string): Promise<Credentials> {
  // The token from the web app is already a JWT — validate and store it
  const graphqlUrl = graphqlUrlOverride || SAILFISH_GRAPHQL_URL;

  // Verify the token works by making a test query
  const response = await fetch(graphqlUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-Sailfish-MCP": "1",
    },
    body: JSON.stringify({
      query: "query { __typename }",
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Token validation failed (HTTP ${response.status}). The token may be expired or invalid.`
    );
  }

  const creds: Credentials = {
    jwt: token,
    graphqlUrl,
    updatedAt: Date.now(),
    source: "web",
    // Re-auth must not forget which agents the user set up
    agents: readCredentials()?.agents,
  };

  writeCredentials(creds);
  return creds;
}
